import React from 'react';
import clsx from 'clsx';

import { Dialog, Autocomplete, Filter } from '@shared/components';
import { getSortedList } from '@utils';

import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import ClearIcon from '@assets/icons/cross-white.svg';
import useStyles from './style';

const defaultState = {
  isSubmitting: false,
  filterType: '',
  selectedSites: [],
  selectedDevices: [],
  selectedNicknames: [],
  selectedAdvancedFilters: [],
};
const PARAMETERS_OPTIONS = ['Site', 'Device Type', 'Device Nickname'];

const FilterComponent = ({
  sites = [],
  deviceTypes = [],
  deviceNicknames = [],
  isOpen = false,
  onClose = () => {},
  onSave = () => {},
}) => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);
  const isSitesSelected = state.filterType === 'Site';
  const isDeviceTypesSelected = state.filterType === 'Device Type';
  const isNicknamesSelected = state.filterType === 'Device Nickname';

  const filterConfig = [
    {
      field: 'Serial',
      fieldToDisplay: 'Serial',
      operatorType: 'string',
    },
    {
      field: 'Extension Number',
      fieldToDisplay: 'Extension Number',
      operatorType: 'string',
    },
    {
      field: 'Extension Type',
      fieldToDisplay: 'Extension Type',
      operatorType: 'string',
    },
    {
      field: 'Extension Name',
      fieldToDisplay: 'Extension Name',
      operatorType: 'string',
    },
    {
      field: 'Extension Email',
      fieldToDisplay: 'Extension Email',
      operatorType: 'string',
    },
    {
      field: 'Extension Status',
      fieldToDisplay: 'Extension Status',
      operatorType: 'string',
    },
    {
      field: 'Device Nick Name',
      fieldToDisplay: 'Device Nickname',
      operatorType: 'string',
    },
    {
      field: 'Model',
      fieldToDisplay: 'Model',
      operatorType: 'string',
    },
  ];

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: option => option.label || '',
  });

  //HANDLERS
  const handleSave = () => {
    let filtersArr = [
      ...state.selectedDevices,
      ...state.selectedNicknames,
      ...state.selectedSites,
      ...state.selectedAdvancedFilters,
    ];
    onSave(filtersArr);
    setState(defaultState);
  };
  const handleClose = () => {
    setState(defaultState);
    onClose();
  };

  //JSX
  const isSaveDisabled =
    !state.selectedDevices.length &&
    !state.selectedNicknames.length &&
    !state.selectedSites.length &&
    !state.selectedAdvancedFilters.length;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      classes={{
        paper: classes.filter,
      }}
    >
      <Dialog.Title hasClose />
      <Dialog.Content className={clsx('p-0', classes.dilalogContent)}>
        <Typography variant='h5' className='bold-font'>
          Filter Options
        </Typography>
        <Typography variant='body1' className='color-text-secondary mt-1'>
          Define filter criteria below.
        </Typography>
        <Typography variant='body1' className='bold-font mt-4'>
          Parameter
        </Typography>
        <Autocomplete
          fullWidth
          disableClearable
          placeholder='Select Parameter Type'
          value={state.filterType}
          options={PARAMETERS_OPTIONS}
          getOptionLabel={option => {
            return option || '';
          }}
          onChange={(event, selectedOption) => {
            setState(prevstate => ({
              ...prevstate,
              filterType: selectedOption,
              selectedSites: [],
              selectedNicknames: [],
              selectedDevices: [],
            }));
          }}
        />
        {isSitesSelected && (
          <>
            <Typography variant='body1' className='bold-font mt-4'>
              Site Name
            </Typography>
            <Autocomplete
              fullWidth
              disableClearable
              getOptionLabel={option => {
                return option.label || '';
              }}
              value={state.selectedSites}
              options={getSortedList(sites)}
              filterOptions={filterOptions}
              onChange={(event, values) => {
                setState(prevState => ({
                  ...prevState,
                  selectedSites: values.slice(),
                }));
              }}
              placeholder='Select Site Name'
              multiple
              classes={{
                tag: classes.siteChip,
              }}
              chipProps={{
                deleteIcon: <img src={ClearIcon} alt='add-filter' />,
                size: 'small',
              }}
            />
          </>
        )}
        {isDeviceTypesSelected && (
          <>
            <Typography variant='body1' className='bold-font mt-4'>
              Device Type
            </Typography>
            <Autocomplete
              fullWidth
              disableClearable
              getOptionLabel={option => {
                return option.label || '';
              }}
              value={state.selectedDevices}
              options={getSortedList(deviceTypes)}
              filterOptions={filterOptions}
              onChange={(event, values) => {
                setState(prevState => ({
                  ...prevState,
                  selectedDevices: values.slice(),
                }));
              }}
              placeholder='Select Device Type'
              multiple
              classes={{
                tag: classes.deviceChip,
              }}
              chipProps={{
                deleteIcon: <img src={ClearIcon} alt='add-filter' />,
                size: 'small',
              }}
            />
          </>
        )}
        {isNicknamesSelected && (
          <>
            <Typography variant='body1' className='bold-font mt-4'>
              Device Nickname
            </Typography>
            <Autocomplete
              fullWidth
              disableClearable
              getOptionLabel={option => {
                return option.label || '';
              }}
              value={state.selectedNicknames}
              options={getSortedList(deviceNicknames)}
              filterOptions={filterOptions}
              onChange={(event, values) => {
                setState(prevState => ({
                  ...prevState,
                  selectedNicknames: values.slice(),
                }));
              }}
              placeholder='Select Device Nickname'
              multiple
              classes={{
                tag: classes.nicknameChip,
              }}
              chipProps={{
                deleteIcon: <img src={ClearIcon} alt='add-filter' />,
                size: 'small',
              }}
            />
          </>
        )}
        <Filter
          filterConfig={filterConfig}
          onFilterApply={selectedAdvancedFilters => {
            setState(prevState => ({
              ...prevState,
              selectedAdvancedFilters,
            }));
          }}
          buttonLabel='Advanced Filtering'
        />
        {state.selectedAdvancedFilters.map(row => {
          let type =
            row.type === 'iRegexp'
              ? 'Contains'
              : row.type === 'iLike'
              ? 'Equals'
              : row.type === 'notILike'
              ? 'Does not equal'
              : 'Does not contain';

          return (
            <Typography vairant='body1' color='primary' className='mt-1'>
              {`${row.field} : ${type} : ${row.value}`}
            </Typography>
          );
        })}
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant='outlined'
          className='bold-font mr-3 mb-4'
          onClick={handleClose}
        >
          <Typography variant='button' className='pl-4 pr-4'>
            Cancel
          </Typography>
        </Button>
        <Button
          disabled={isSaveDisabled}
          variant='contained'
          color='primary'
          className='bold-font mr-4 mb-4 pl-1 pr-1'
          onClick={handleSave}
        >
          <Typography variant='button' className='pl-8 pr-8'>
            Save
          </Typography>
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
export default FilterComponent;
